import React, { useCallback, useRef, useState } from 'react';
import { MdContentCopy, MdDelete, MdDriveFileRenameOutline, MdMoreVert } from 'react-icons/md';
import {
  CHIP_MENU_ITEM_TONE_DANGER,
  ChipMenu,
  IconButton,
} from '@aibuild/ui';
import { useEditorContext } from '../useEditorContext';
import { QuickMenuAnchor } from './NodeQuickMenu.styled';

export interface NodeQuickMenuProps {
  readonly nodeId: string;
  /** Fired when the user picks "Rename". The parent enables inline title editing. */
  readonly onRenameRequest?: () => void;
}

const NodeQuickMenu: React.FC<NodeQuickMenuProps> = ({ nodeId, onRenameRequest }) => {
  const { duplicateNode, removeNode, readOnly } = useEditorContext();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const onClose = useCallback(() => setOpen(false), []);
  const onDuplicate = useCallback(() => {
    setOpen(false);
    duplicateNode(nodeId);
  }, [duplicateNode, nodeId]);
  const onDelete = useCallback(() => {
    setOpen(false);
    removeNode(nodeId);
  }, [removeNode, nodeId]);
  const onRenameClick = useCallback(() => {
    setOpen(false);
    onRenameRequest?.();
  }, [onRenameRequest]);

  if (readOnly) return null;

  return (
    <QuickMenuAnchor className="nodrag">
      <IconButton
        ref={triggerRef}
        ariaLabel="Node options"
        icon={<MdMoreVert />}
        size="sm"
        variant="ghost"
        isOpen={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        dataTestId={`process-node__menu-trigger-${nodeId}`}
      />
      {open && (
        <ChipMenu
          onClose={onClose}
          align="right"
          portal
          anchorRef={triggerRef}
          dataTestId={`process-node__menu-${nodeId}`}
        >
          <ChipMenu.List>
            {onRenameRequest ? (
              <ChipMenu.Item
                icon={<MdDriveFileRenameOutline />}
                onClick={onRenameClick}
                dataTestId={`process-node__menu-rename-${nodeId}`}
              >
                Rename
              </ChipMenu.Item>
            ) : null}
            <ChipMenu.Item
              icon={<MdContentCopy />}
              onClick={onDuplicate}
              dataTestId={`process-node__menu-duplicate-${nodeId}`}
            >
              Duplicate
            </ChipMenu.Item>
            <ChipMenu.Item
              icon={<MdDelete />}
              tone={CHIP_MENU_ITEM_TONE_DANGER}
              onClick={onDelete}
              dataTestId={`process-node__menu-delete-${nodeId}`}
            >
              Delete
            </ChipMenu.Item>
          </ChipMenu.List>
        </ChipMenu>
      )}
    </QuickMenuAnchor>
  );
};

export default NodeQuickMenu;
